/**
 * Main application component for Learning Companion 
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { VoiceLiveClient } from './services/voiceLiveClient';
import { PronunciationWebSocketClient } from './services/pronunciationWebSocketClient';
import { storyContextService } from './services/storyContextService';
import { uiStateManager, UIEventHandler } from './services/uiStateService';
import { backendVoiceLiveService } from './services/backendVoiceLiveService';
import { readingProgressService } from './services/readingProgressService';
import { AgeProfile, getAgeProfile } from './config/ageProfiles.config';
import { getApiUrl } from './utils/apiUtils';
import { createVoiceLiveConfig } from './config/azure.config';
import { AudioProcessor } from './utils/audioUtils';
import RoleSelector from './components/RoleSelector';
import AgeSelector from './components/AgeSelector';
import WelcomeScreen from './components/WelcomeScreen';
import MicButton from './components/MicButton';
import ConversationPane from './components/ConversationPane';
import { ConnectionStatusIndicator } from './components/ConnectionStatusIndicator';
import StoryPane from './components/StoryPane';
import StatusIndicator from './components/StatusIndicator';
import { HomeIcon, ChatIcon, MonitorIcon, PowerIcon } from './components/SvgConsolidated/SvgIcons';
import TopicDriftIndicator from './components/TopicDriftIndicator';
import ChatButtonTooltip from './components/ChatButtonTooltip';
import EnhancedTeacherDashboard from './components/EnhancedTeacherDashboard';
import VersionDisplay from './components/VersionDisplay';
import ApiTestComponent from './components/ApiTestComponent';
import QuizDialog from './components/QuizDialog';
import { ConversationMessage } from './types/conversation.types';
import { VoiceLiveConnectionState, VoiceLiveConversationState } from './types/voiceLive.types';
import { Story, StoryReadingState } from './types/story.types';
import { logger } from './utils/logger';
import { requiresUserGestureForMicrophone, getBrowserName } from './utils/browserDetection';

console.log('🚀🚀🚀 APP.TSX LOADED - FRESH BUILD VERIFICATION - TIMESTAMP: ' + new Date().toISOString() + ' 🚀🚀🚀');

export default function App() {
  // User role and age
  const [userRole, setUserRole] = useState<'teacher' | 'student' | null>(null);
  const [userAge, setUserAge] = useState<number | null>(null);
  const [ageProfile, setAgeProfile] = useState<AgeProfile | null>(null);
  
  // Quiz dialog preference (persisted in localStorage)
  const [quizDialogEnabled, setQuizDialogEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('quiz_dialog_enabled');
      return stored ? JSON.parse(stored) : false; // Default: disabled
    } catch {
      return false;
    }
  });
  
  // Role Ranger preference (persisted in localStorage)
  const [roleRangerEnabled, setRoleRangerEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('role_ranger_enabled');
      return stored ? JSON.parse(stored) : true; // Default: enabled
    } catch {
      return true;
    }
  });
  
  // Persist quiz dialog preference to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('quiz_dialog_enabled', JSON.stringify(quizDialogEnabled));
    } catch (error) {
      console.error('Failed to save quiz dialog preference:', error);
    }
  }, [quizDialogEnabled]);
  
  // Persist role ranger preference to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('role_ranger_enabled', JSON.stringify(roleRangerEnabled));
    } catch (error) {
      console.error('Failed to save role ranger preference:', error);
    }
  }, [roleRangerEnabled]);
  
  // VoiceLive client
  const [voiceLiveClient] = useState<VoiceLiveClient>(() => {
    const client = new VoiceLiveClient();
    // Set up bidirectional reference for announcement control
    backendVoiceLiveService.setVoiceLiveClient(client);
    return client;
  });
  const [connectionState, setConnectionState] = useState<VoiceLiveConnectionState>('disconnected');
  const [conversationState, setConversationState] = useState<VoiceLiveConversationState>('idle');
  const [hasEverConnected, setHasEverConnected] = useState(false);
  
  // Pronunciation WebSocket client ref (managed by StoryPane, used by MicButton)
  const pronunciationWsClientRef = useRef<PronunciationWebSocketClient | null>(null);
  
  // Callback to send pronunciation audio frames
  const handlePronunciationFrame = useCallback((pcmData: ArrayBuffer) => {
    if(pcmData.byteLength > 10000){
      console.log(`🎤 [App] Pronunciation audio frame received: ${pcmData.byteLength} bytes`);
    }
    if (pronunciationWsClientRef.current && pronunciationWsClientRef.current.isConnected()) {
      if(pcmData.byteLength > 10000){
        console.log('✅ [App] Sending to pronunciation WebSocket');
      }
      pronunciationWsClientRef.current.sendAudioFrame(pcmData);
    } else {
      console.warn('⚠️ [App] Pronunciation WebSocket not connected, skipping frame');
    }
  }, []);
  
  // Audio playback
  const [audioProcessor] = useState<AudioProcessor>(() => new AudioProcessor());
  
  // Conversation
  const [conversationId] = useState<string>(() => `conversation_${Date.now()}`);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  
  // Sync preferences to backend when they change
  useEffect(() => {
    const syncPreferences = async () => {
      if (!conversationId) return;
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/voicelive/session/${conversationId}/preferences`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              roleRangerEnabled,
              quizDialogEnabled,
            }),
          }
        );
        
        if (!response.ok) {
          console.warn(`Failed to sync preferences: ${response.status}`);
        }
      } catch (error) {
        console.error('Error syncing preferences to backend:', error);
      }
    };
    
    syncPreferences();
  }, [roleRangerEnabled, quizDialogEnabled, conversationId]);
  
  // UI state

  const [showApiTest, setShowApiTest] = useState(false);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [isTopicDrifting, setIsTopicDrifting] = useState(false);
  const [showConversationPanel, setShowConversationPanel] = useState(false);
  const [showChatTooltip, setShowChatTooltip] = useState(false);
  // const [showReadyNotification, setShowReadyNotification] = useState(false);
  const [isContinuousListening, setIsContinuousListening] = useState(false);
  const [isContinuousMode, setIsContinuousMode] = useState(false);
  const [microphonePosition, setMicrophonePosition] = useState<'middle-right' | 'bottom-right' | 'bottom-center'>('middle-right');
  const [microphoneFloating, setMicrophoneFloating] = useState(true);
  const [topicDriftData, setTopicDriftData] = useState({
    contentSimilarity: 0,
    storyRelevance: 0,
    driftScore: 0
  });
  
  // Quiz state
  const [quizQuestion, setQuizQuestion] = useState<{
    conversation_id: string;
    question_id: number;
    question_text: string;
    options: string[];
    correct_answer: string;
    difficulty: string;
    question_type: string;
    milestone: string;
  } | null>(null);
  const [quizVisible, setQuizVisible] = useState(false);
  const [quizAnswerStartTime, setQuizAnswerStartTime] = useState<number | null>(null);
  const [isValidatingQuiz, setIsValidatingQuiz] = useState(false);
  const [quizValidationResult, setQuizValidationResult] = useState<'correct' | 'incorrect' | null>(null);
  const [quizCompleting, setQuizCompleting] = useState(false); // Tracks if completion phrase detected
  const [quizQuestionQueue, setQuizQuestionQueue] = useState<any[]>([]); // Queue of questions from backend
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // Index in queue
  
  // Story state
  const [currentStory, setCurrentStory] = useState<Story | null>(null);
  const [readingState, setReadingState] = useState<StoryReadingState>({
    isReadingMode: false,
    currentStory: null,
    readingProgress: 0
  });
  
  // Safari-specific state for user gesture requirement
  const [showStartReadingButton, setShowStartReadingButton] = useState(false);
  const [pendingStoryForMicAccess, setPendingStoryForMicAccess] = useState<Story | null>(null);
  
  // ✅ NEW: Set audio processor reference in voiceLiveClient for playback completion tracking
  useEffect(() => {
    voiceLiveClient.setAudioProcessor(audioProcessor);
  }, [voiceLiveClient, audioProcessor]);
  
  // Initialize age profile when user age changes
  useEffect(() => {
    if (userAge !== null) {
      const profile = getAgeProfile(userAge);
      setAgeProfile(profile);
      logger.logAgeAdaptationEvent('age_profile_selected', {
        age: userAge,
        profile_name: profile.name
      }, userAge, conversationId);
      
      // Show chat tooltip for new users after a short delay
      const tooltipTimer = setTimeout(() => {
        setShowChatTooltip(true);
      }, 2000);
      
      return () => clearTimeout(tooltipTimer);
    }
  }, [userAge, conversationId]);

  // Initialize UI State Service - only connect after user has selected role
  useEffect(() => {
    if (conversationId && userRole) { // Only connect after role is selected
      const uiStateService = uiStateManager.getInstance(conversationId);
      
      const eventHandlers: UIEventHandler = {
        onFloatingPanelControl: (visible: boolean, triggerSource: string) => {
          console.log(`Floating panel control: ${visible} (source: ${triggerSource})`);
          setShowConversationPanel(visible);
        },
        
        onMicrophoneControl: (position: string, state: string, floating: boolean) => {
          console.log(`Microphone control: ${position}, ${state}, floating: ${floating}`);
          setMicrophonePosition(position as 'middle-right' | 'bottom-right' | 'bottom-center');
          setMicrophoneFloating(floating);
        },
        
        onUIStateUpdate: (state) => {
          console.log('UI state update:', state);
          // Only update panel visibility if it's explicitly being controlled
          // Don't automatically show panel for every state update
          if (state.floating_panel_visible !== undefined) {
            setShowConversationPanel(state.floating_panel_visible);
          }
          setMicrophonePosition(state.microphone_position);
          setMicrophoneFloating(state.microphone_floating !== false);
        },
        
        onTopicDriftDetected: (driftData) => {
          console.log('Topic drift detected:', driftData);
          setIsTopicDrifting(true);
          setTopicDriftData({
            contentSimilarity: driftData.content_similarity || 0,
            storyRelevance: driftData.story_relevance_score || 0,
            driftScore: driftData.drift_score || 0
          });
        },
        
        onTopicDriftCleared: (data) => {
          console.log('Topic drift cleared:', data);
          setIsTopicDrifting(false);
        },
        
        onQuizQuestionDisplay: (quizData) => {
          console.log('Quiz question received:', quizData);
          setQuizQuestion(quizData);
          setQuizVisible(true);
          setQuizAnswerStartTime(Date.now());
        }
      };

      // Connect to UI state service (will fail gracefully if backend unavailable)
      uiStateService.connect(eventHandlers).catch(error => {
        console.warn('Failed to connect to UI state service - using local state management:', error);
      });

      return () => {
        uiStateManager.cleanup(conversationId);
      };
    }
  }, [conversationId, userRole]); // Added userRole dependency
  
  // Set up VoiceLive event listeners
  useEffect(() => {
    const handleConnectionStateChange = (state: VoiceLiveConnectionState) => {
      setConnectionState(state);
      
      // Track when user has successfully connected
      if (state === 'connected') {
        setHasEverConnected(true);
      }
      
      if (state === 'error') {
        setCurrentError('Connection failed. Please check your configuration.');
      }
    };
    
    const handleConversationStateChange = (state: VoiceLiveConversationState) => {
      setConversationState(state);
      if (state === 'listening') {
        setIsRecording(true);
      } else {
        setIsRecording(false);
      }
    };

    const handleContinuousListeningChange = (isListening: boolean) => {
      setIsContinuousListening(isListening);
    };
    
    const handleUserTranscription = (transcript: string, itemId: string) => {
      console.log('👤 USER TRANSCRIPT RECEIVED IN APP.TSX HANDLEUSERTRANSCRIPTION:');
      console.log('   📝 Transcript content:', transcript);
      console.log('   🆔 Item ID:', itemId);
      console.log('   🔤 Transcript length:', transcript?.length || 0);
      console.log('   � User age:', userAge);
      console.log('   �🚀 handleUserTranscription function is being called successfully!');
      
      // Validate transcript content
      if (!transcript || transcript.trim() === '') {
        console.warn('   ⚠️ Empty or invalid transcript received - skipping message creation');
        return;
      }
      
      const message: ConversationMessage = {
        id: itemId,
        type: 'user',
        content: transcript,
        timestamp: new Date(),
        userAge
      };
      
      console.log('   📦 Created conversation message:', message);
      
      setMessages(prev => {
        console.log('   📊 BEFORE - Current messages count:', prev.length);
        console.log('   📊 BEFORE - Last few messages:', prev.slice(-2));
        
        const newMessages = [...prev, message];
        
        console.log('   📊 AFTER - New messages count:', newMessages.length);
        console.log('   📊 AFTER - Last few messages:', newMessages.slice(-2));
        console.log('   ✅ User message successfully added to conversation state!');
        
        return newMessages;
      });
      
      console.log('   🎯 handleUserTranscription completed successfully!');
    };
    
    const handleAssistantTranscriptDelta = (delta: string, itemId: string) => {
      setMessages(prev => {
        const existingIndex = prev.findIndex(msg => msg.id === itemId);
        if (existingIndex >= 0) {
          // Update existing message
          const updated = [...prev];
          const updatedContent = updated[existingIndex].content + delta;
          updated[existingIndex] = {
            ...updated[existingIndex],
            content: updatedContent
          };
          
          // Check for unified completion phrase "Happy Reading!" for both QuizCritter and RoleRanger
          if (quizVisible && updatedContent.toLowerCase().includes('happy reading')) {
            console.log('🎯 Completion phrase "Happy Reading!" detected - disabling quiz buttons');
            setQuizCompleting(true);
          }
          
          return updated;
        } else {
          // Create new message
          const message: ConversationMessage = {
            id: itemId,
            type: 'assistant',
            content: delta,
            timestamp: new Date(),
            userAge
          };
          console.log('� New assistant message:', message.content);
          return [...prev, message];
        }
      });
    };
    
    const handleError = (error: any) => {
      handleConnectionError(error);
      logger.logErrorEvent('voiceLive_client_error', new Error(error.message), {
        error_type: error.type
      }, userAge || undefined, conversationId);
    };
    
    const handleRecordingTooShort = (data: { duration: number; minimum: number }) => {
      console.warn(`Recording too short: ${data.duration}ms (minimum: ${data.minimum}ms)`);
      
      // Show user feedback through a temporary error message that clears itself
      const friendlyMessage = `Please speak for at least ${Math.ceil(data.minimum / 100) / 10} seconds for better voice recognition.`;
      setCurrentError(friendlyMessage);
      
      // Auto-clear the message after 3 seconds since it's more guidance than an error
      setTimeout(() => {
        setCurrentError(null);
      }, 3000);
      
      // Reset recording state since the audio wasn't committed
      setIsRecording(false);
      
      logger.logAudioEvent('recording_too_short_feedback', {
        duration_ms: data.duration,
        minimum_required_ms: data.minimum
      }, userAge || undefined, conversationId);
    };
    
    const handleAudioResponse = async (base64AudioData: string) => {
      try {
        // Initialize audio context if needed
        if (!audioProcessor.isAudioContextInitialized()) {
          await audioProcessor.initializeAudioContext();
        }
        
        // Play the received audio delta
        await audioProcessor.playAudioFromBase64(base64AudioData);
        
        logger.logAudioEvent('audio_response_queued', {
          data_length: base64AudioData.length
        }, userAge || undefined, conversationId);
      } catch (error) {
        console.error('Error playing audio response:', error);
        logger.logErrorEvent('audio_playback_error', error as Error, {
          data_length: base64AudioData.length
        }, userAge || undefined, conversationId);
      }
    };

    const handleSpeechStarted = (itemId: string) => {
      const uiStateService = uiStateManager.getInstance(conversationId);
      uiStateService.sendVoiceLiveEvent('speech_started', { item_id: itemId });
      
      // Don't automatically show conversation panel - let the user control this
      // Only show if it's already visible or if the user has explicitly requested it
      // setShowConversationPanel(true); // Commented out to prevent random popups
      setIsRecording(true);
    };

    const handleSpeechStopped = (itemId: string) => {
      const uiStateService = uiStateManager.getInstance(conversationId);
      uiStateService.sendVoiceLiveEvent('speech_stopped', { item_id: itemId });
      setIsRecording(false);
    };

    const handleResponseStarted = (responseId: string) => {
      // Clear any pending audio from previous responses
      audioProcessor.clearAudioQueue();
      
      const uiStateService = uiStateManager.getInstance(conversationId);
      uiStateService.sendVoiceLiveEvent('response_started', { response_id: responseId });
      
      logger.logVoiceLiveEvent('response_started', {
        response_id: responseId
      }, userAge || undefined, conversationId);
    };

    const handleResponseCompleted = (responseId: string) => {
      const uiStateService = uiStateManager.getInstance(conversationId);
      uiStateService.sendVoiceLiveEvent('response_completed', { response_id: responseId });
      
      // Local fallback - auto-hide conversation panel after 3 seconds
      setTimeout(() => {
        setShowConversationPanel(false);
      }, 3000);
    };

    const handleConnectionError = (error: any) => {
      const uiStateService = uiStateManager.getInstance(conversationId);
      uiStateService.sendVoiceLiveEvent('connection_error', { error: error.message });
      setCurrentError(error.message || 'An error occurred');
    };

    const handleReadingProgress = (progressData: any) => {
      console.log('📖 Reading progress received in App:', progressData);
      // Pass to reading progress service for distribution to components
      readingProgressService.handleProgressUpdate(progressData);
    };
    
    const handlePersonaSwitched = (personaData: any) => {
      console.log('🎭 Persona switched in App:', personaData);
      // If switching TO quiz_critter, reset queue to prepare for new quiz
      if (personaData.persona === 'quiz_critter') {
        console.log('🎮 QuizCritter activated - resetting question queue');
        setQuizQuestionQueue([]);
        setCurrentQuestionIndex(0);
      }
      // If switching AWAY from quiz_critter (to any other persona), close the quiz dialog immediately
      else if (personaData.from_persona === 'quiz_critter') {
        console.log('📤 QuizCritter completed - closing quiz dialog and resetting state');
        setQuizVisible(false);
        setQuizQuestion(null);
        setQuizValidationResult(null);
        setIsValidatingQuiz(false);
        setQuizCompleting(false);
        setQuizQuestionQueue([]);
        setCurrentQuestionIndex(0);
      }
    };
    
    // Fallback handler for persona_returned event (older format)
    const handlePersonaReturned = (returnData: any) => {
      console.log('🎭 Persona returned in App:', returnData);
      // If returning from quiz_critter to gentle_teacher, close quiz dialog
      if (returnData.from_persona === 'quiz_critter') {
        console.log('📤 QuizCritter returned to GentleTeacher - closing quiz dialog and resetting state');
        setQuizVisible(false);
        setQuizQuestion(null);
        setQuizValidationResult(null);
        setIsValidatingQuiz(false);
        setQuizCompleting(false);
        setQuizQuestionQueue([]);
        setCurrentQuestionIndex(0);
      }
    };
    
    const handleQuizQuestionDisplay = (quizData: any) => {
      console.log('🎮 Quiz question received in App:', quizData);
      
      // ✅ NEW DESIGN: Backend sends ALL quiz questions at once
      // Frontend queues them and shows one at a time as user answers
      if (quizData.question_text && quizData.options) {
        const newQuestion = {
          conversation_id: conversationId,
          question_id: quizData.question_id,
          question_text: quizData.question_text,
          options: quizData.options,
          correct_answer: quizData.correct_answer,
          difficulty: quizData.difficulty || 'medium',
          question_type: quizData.question_type || 'factual',
          milestone: quizData.milestone || '25%'
        };
        
        // Add to queue
        setQuizQuestionQueue(prev => {
          // If this is the first question, reset queue and show immediately
          if (prev.length === 0) {
            setCurrentQuestionIndex(0);
            setQuizQuestion(newQuestion);
            setQuizVisible(true);
            setQuizAnswerStartTime(Date.now());
            console.log('✅ Quiz started with first question:', newQuestion.question_id);
            return [newQuestion];
          } else {
            // Add subsequent questions to queue
            const updated = [...prev, newQuestion];
            console.log(`� Question ${updated.length} queued:`, newQuestion.question_id);
            return updated;
          }
        });
      }
    };
    
    // Add event listeners
    voiceLiveClient.addEventListener('connectionStateChange', handleConnectionStateChange);
    voiceLiveClient.addEventListener('conversationStateChange', handleConversationStateChange);
    voiceLiveClient.addEventListener('continuousListeningStateChange', handleContinuousListeningChange);
    voiceLiveClient.addEventListener('userTranscriptionCompleted', handleUserTranscription);
    voiceLiveClient.addEventListener('assistantTranscriptDelta', handleAssistantTranscriptDelta);
    voiceLiveClient.addEventListener('audioResponse', handleAudioResponse);
    voiceLiveClient.addEventListener('responseStarted', handleResponseStarted);
    voiceLiveClient.addEventListener('responseCompleted', handleResponseCompleted);
    voiceLiveClient.addEventListener('speechStarted', handleSpeechStarted);
    voiceLiveClient.addEventListener('speechStopped', handleSpeechStopped);
    voiceLiveClient.addEventListener('recordingTooShort', handleRecordingTooShort);
    voiceLiveClient.addEventListener('error', handleError);
    voiceLiveClient.addEventListener('readingProgress', handleReadingProgress);
    voiceLiveClient.addEventListener('personaSwitched', handlePersonaSwitched);
    voiceLiveClient.addEventListener('personaReturned', handlePersonaReturned); // Fallback for older format
    voiceLiveClient.addEventListener('quizQuestionDisplay', handleQuizQuestionDisplay);
    
    // Cleanup
    return () => {
      voiceLiveClient.removeEventListener('connectionStateChange', handleConnectionStateChange);
      voiceLiveClient.removeEventListener('conversationStateChange', handleConversationStateChange);
      voiceLiveClient.removeEventListener('continuousListeningStateChange', handleContinuousListeningChange);
      voiceLiveClient.removeEventListener('userTranscriptionCompleted', handleUserTranscription);
      voiceLiveClient.removeEventListener('assistantTranscriptDelta', handleAssistantTranscriptDelta);
      voiceLiveClient.removeEventListener('audioResponse', handleAudioResponse);
      voiceLiveClient.removeEventListener('responseStarted', handleResponseStarted);
      voiceLiveClient.removeEventListener('responseCompleted', handleResponseCompleted);
      voiceLiveClient.removeEventListener('speechStarted', handleSpeechStarted);
      voiceLiveClient.removeEventListener('speechStopped', handleSpeechStopped);
      voiceLiveClient.removeEventListener('recordingTooShort', handleRecordingTooShort);
      voiceLiveClient.removeEventListener('error', handleError);
      voiceLiveClient.removeEventListener('readingProgress', handleReadingProgress);
      voiceLiveClient.removeEventListener('personaSwitched', handlePersonaSwitched);
      voiceLiveClient.removeEventListener('personaReturned', handlePersonaReturned);
      voiceLiveClient.removeEventListener('quizQuestionDisplay', handleQuizQuestionDisplay);
    };
  }, [voiceLiveClient, userAge, conversationId, audioProcessor, quizVisible]);
  
  // Cleanup audio processor on unmount
  useEffect(() => {
    return () => {
      audioProcessor.cleanup();
    };
  }, [audioProcessor]);

  // Auto-trigger welcome announcement when initialization completes
  useEffect(() => {
    // Trigger welcome announcement when user completes initialization and reaches main story UI
    if (connectionState === 'connected' && hasEverConnected && userAge && ageProfile) {
      // Small delay to ensure UI has fully transitioned
      const triggerTimer = setTimeout(async () => {
        try {
          // Use backend-controlled announcement for welcome message with available stories
          await handleWelcomeAnnouncementWithBackend(userAge, conversationId);
          
          logger.logUIEvent('auto_welcome_announcement_triggered', {
            connection_state: connectionState,
            has_ever_connected: hasEverConnected,
            age: userAge,
            age_category: ageProfile.category,
            backend_controlled: true
          }, userAge, conversationId);
          
        } catch (error) {
          console.error('❌ Failed to trigger automatic welcome announcement:', error);
          // Log full error details to help debugging
          if (error instanceof Error) {
            console.error('Error name:', error.name);
            console.error('Error message:', error.message);
            console.error('Error stack:', error.stack);
          }
          logger.logErrorEvent('auto_welcome_announcement_failed', error as Error, {
            connection_state: connectionState,
            age: userAge
          }, userAge, conversationId);
          
          // Fallback to direct VoiceLive trigger if backend fails
          try {
            await fetch(getApiUrl(`/config/trigger-introduction?conversation_id=${conversationId}&user_age=${userAge}`), {
              method: 'POST'
            });
            voiceLiveClient.triggerStoryIntroduction();
          } catch (fallbackError) {
            console.error('Fallback announcement also failed:', fallbackError);
          }
        }
      }, 1500); // 1.5 second delay to ensure smooth transition

      return () => clearTimeout(triggerTimer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionState, hasEverConnected, userAge, ageProfile, voiceLiveClient, conversationId]);
  
  // Handle role selection
  const handleRoleSelected = (role: 'teacher' | 'student') => {
    setUserRole(role);
    if (role === 'teacher') {
      // Teachers don't need age selection, go directly to dashboard
      setUserAge(0); // Default age for teacher context
    }
  };

  // Handle returning to home/role selection
  const handleReturnToHome = () => {
    // Reset all states to initial values
    setUserRole(null);
    setUserAge(null);
    setAgeProfile(null);
    setMessages([]);
    setCurrentStory(null);
    setCurrentError(null);
    setShowConversationPanel(false);
    setShowApiTest(false);
    setIsTopicDrifting(false);
    setShowChatTooltip(false);
    // setShowReadyNotification(false);
    setIsContinuousListening(false);
    setIsContinuousMode(false);
    
    // Disconnect VoiceLive client if connected
    if (voiceLiveClient.isConnected()) {
      voiceLiveClient.disconnect();
    }
    
    // Clear audio processor
    audioProcessor.cleanup();
    
    // Reset reading state
    setReadingState({
      isReadingMode: false,
      currentStory: null,
      readingProgress: 0
    });
    
    console.log('🏠 Returned to home screen - all states reset');
  };
  
  // Connect to VoiceLive when age is selected
  const handleAgeSelected = async (age: number) => {
    setUserAge(age);
    setCurrentError(null);
    
    try {
      // Step 1: Create backend session first for better control
      logger.logVoiceLiveEvent('backend_session_creation_started', {
        age: age,
        conversation_id: conversationId,
        has_reading_state: readingState.isReadingMode
      }, age, conversationId);

      const backendSession = await backendVoiceLiveService.createSession({
        conversation_id: conversationId,
        age: age,
        story_context: readingState.currentStory?.title,
        custom_instructions: readingState.isReadingMode ? 'You are helping with story reading and comprehension.' : undefined
      });

      logger.logVoiceLiveEvent('backend_session_created', {
        session_success: backendSession.success,
        conversation_id: backendSession.conversation_id,
        websocket_url_provided: !!backendSession.websocket_url
      }, age, conversationId);

      // Check if backend session creation was successful
      if (!backendSession.success) {
        throw new Error('Backend session creation failed');
      }

      // Step 2: Create frontend config using backend session info
      const config = await createVoiceLiveConfig(conversationId, age);
      
      // Override with backend-provided configuration if available
      if (backendSession.session_config) {
        config.session_config = {
          ...config.session_config,
          ...backendSession.session_config
        };
      }
      
      // Adjust config for reading mode if active
      if (readingState.isReadingMode) {
        const readingConfigResponse = await fetch(getApiUrl(`/stories/age/${age}/reading-config`));
        if (readingConfigResponse.ok) {
          const readingConfig = await readingConfigResponse.json();
          config.session_config = {
            ...config.session_config,
            ...readingConfig.turn_detection && { turn_detection: readingConfig.turn_detection },
            instructions: readingState.currentStory 
              ? `${config.session_config.instructions}\n\n${readingConfig.reading_mode_instructions}`
              : config.session_config.instructions
          };
        }
      }
      
      // Step 3: Connect VoiceLive client with enhanced config
      await voiceLiveClient.connect(config);
      
      logger.logConnectionEvent('connection_initiated', {
        age: age,
        conversation_id: conversationId,
        reading_mode: readingState.isReadingMode,
        story_id: readingState.currentStory?.id
      }, age, conversationId);
    } catch (error) {
      // Enhanced error handling with user-friendly messages
      let errorMessage = 'Failed to connect to VoiceLive service.';
      
      if (error instanceof Error) {
        // Check for specific error messages from backend
        if (error.message.includes('Azure Voice Live not configured')) {
          errorMessage = '⚙️ Voice features are currently unavailable.\n\n' +
                        'The voice conversation service requires Azure credentials to be configured. ' +
                        'Please contact your administrator or check the backend configuration.\n\n' +
                        '📊 Note: You can still access the Teacher Dashboard and other features.';
        } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
          errorMessage = '🔧 Server Configuration Required\n\n' +
                        'The voice service is not yet configured. The backend needs Azure Voice Live credentials.\n\n' +
                        '✅ Other features (Teacher Dashboard, Analytics, Stories) are still available.';
        } else if (error.message.includes('Network') || error.message.includes('Failed to fetch')) {
          errorMessage = '🌐 Connection Error\n\n' +
                        'Unable to reach the backend server. Please check:\n' +
                        '• Backend server is running\n' +
                        '• Network connection is stable\n' +
                        '• Backend URL is correct: ' + process.env.REACT_APP_API_BASE_URL;
        } else {
          errorMessage = `❌ Connection Failed\n\n${error.message}\n\nPlease try again or contact support if the issue persists.`;
        }
      }
      
      // IMPORTANT: Set connection state to 'error' so WelcomeScreen shows the error
      setConnectionState('error');
      setCurrentError(errorMessage);
      
      logger.logErrorEvent('connection_failed', error as Error, {
        age: age,
        error_type: error instanceof Error ? error.constructor.name : 'unknown',
        error_message: error instanceof Error ? error.message : String(error)
      }, age, conversationId);
    }
  };
  
  // Handle welcome announcement with available stories using backend VoiceLive control
  const handleWelcomeAnnouncementWithBackend = useCallback(async (userAge: number, conversationId: string) => {
    try {
      logger.logVoiceLiveEvent('backend_welcome_announcement_started', {
        age: userAge,
        conversation_id: conversationId,
        announcement_type: 'welcome_with_stories',
        backend_controlled: true
      }, userAge, conversationId);

      // Get client's current time hour (0-23)
      const currentHour = new Date().getHours();
      logger.logVoiceLiveEvent('client_time_captured', {
        hour: currentHour,
        conversation_id: conversationId
      }, userAge, conversationId);

      // Step 1: Start welcome announcement with VAD disabled on backend
      const welcomeResponse = await backendVoiceLiveService.startWelcomeAnnouncement({
        conversation_id: conversationId,
        age: userAge,
        include_stories: true,
        announcement_duration_ms: 12000, // Allow 12 seconds for welcome + story list announcement
        client_time_hour: currentHour // Pass client's local time for greeting
      });

      // Log the welcome message for debugging
      if (welcomeResponse.announcement_config?.welcome_message) {
        console.log('📢 Backend welcome message:', welcomeResponse.announcement_config.welcome_message);
        console.log('✅ Backend will handle welcome announcement - frontend waiting for completion');
      }

      // Step 2: Backend already sent welcome message and triggered response.create()
      // Frontend should NOT send duplicate message - just wait for backend's announcement to complete
      // REMOVED: voiceLiveClient.triggerCustomAnnouncement() call to prevent duplicate messages
      
      // Step 3: Wait for AI response completion, then restore VAD
      const handleWelcomeAnnouncementComplete = async () => {
        // Remove listener after use
        voiceLiveClient.removeEventListener('responseCompleted', handleWelcomeAnnouncementComplete);
        
        try {
          // Restore VAD on backend after announcement completes
          await backendVoiceLiveService.completeStoryAnnouncement(conversationId, userAge);
          
          console.log('✅ Welcome announcement completed with backend control - ready for story selection!');
          
          logger.logVoiceLiveEvent('backend_welcome_announcement_completed', {
            age: userAge,
            conversation_id: conversationId,
            announcement_type: 'welcome_with_stories',
            vad_restored: true,
            backend_controlled: true
          }, userAge, conversationId);
          
        } catch (error) {
          logger.logErrorEvent('backend_welcome_announcement_completion_failed', error as Error, {
            conversation_id: conversationId
          }, userAge, conversationId);
        }
      };

      // Set up listener for welcome announcement completion
      voiceLiveClient.addEventListener('responseCompleted', handleWelcomeAnnouncementComplete);

      logger.logVoiceLiveEvent('backend_welcome_announcement_setup_completed', {
        age: userAge,
        conversation_id: conversationId,
        vad_disabled_on_server: true,
        awaiting_response_completion: true
      }, userAge, conversationId);

    } catch (error) {
      logger.logErrorEvent('backend_welcome_announcement_failed', error as Error, {
        age: userAge,
        conversation_id: conversationId
      }, userAge, conversationId);
      throw error;
    }
  }, [voiceLiveClient]);

  // Handle story announcements with backend VoiceLive control
  const handleStoryAnnouncementWithBackend = useCallback(async (story: Story, userAge: number, conversationId: string) => {
    try {
      logger.logStoryEvent('backend_announcement_flow_started', {
        story_id: story.id,
        story_title: story.title,
        backend_controlled: true
      }, userAge, conversationId);

      // Step 0: Fetch age-appropriate announcement text from backend
      let announcementText: string | undefined;
      try {
        const announcementResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/story-context/get-announcement`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storyId: story.id,
            userAge: userAge,
            conversationId: conversationId
          })
        });
        
        if (announcementResponse.ok) {
          const announcementData = await announcementResponse.json();
          announcementText = announcementData.announcement_text;
          console.log('📢 Fetched age-appropriate announcement:', announcementText);
        } else {
          console.warn('⚠️ Failed to fetch announcement text, will use fallback');
        }
      } catch (announcementError) {
        console.warn('⚠️ Error fetching announcement text:', announcementError);
      }

      // Step 1: Start announcement with VAD disabled on backend and initialize reading tracker
      await backendVoiceLiveService.startStoryAnnouncement({
        conversation_id: conversationId,
        story_id: story.id,
        story_title: story.title,
        age: userAge,
        announcement_duration_ms: 8000, // Allow 8 seconds for story announcement
        story_text: story.content, // Include story text for reading progress tracker initialization
        announcement_text: announcementText // Pass age-appropriate announcement text
      });

      // Step 2: Trigger the story introduction through VoiceLive (VAD is now disabled on server)
      voiceLiveClient.triggerStoryIntroduction();

      // Step 3: Wait for AI response completion, then restore VAD
      const handleAnnouncementComplete = async () => {
        // Remove listener after use
        voiceLiveClient.removeEventListener('responseCompleted', handleAnnouncementComplete);
        
        try {
          // Restore VAD on backend after announcement completes
          await backendVoiceLiveService.completeStoryAnnouncement(conversationId, userAge);
          
          // DO NOT auto-start listening - keep in push-to-talk mode
          // User must explicitly click the mic button to start reading
          console.log('✅ Story announcement completed - ready for push-to-talk interaction');
          
          logger.logStoryEvent('backend_announcement_flow_completed', {
            story_id: story.id,
            story_title: story.title,
            continuous_listening_enabled: false,
            push_to_talk_mode: true,
            backend_controlled: true
          }, userAge, conversationId);
          
        } catch (error) {
          logger.logErrorEvent('backend_announcement_completion_failed', error as Error, {
            story_id: story.id,
            conversation_id: conversationId
          }, userAge, conversationId);
        }
      };

      // Set up listener for announcement completion
      voiceLiveClient.addEventListener('responseCompleted', handleAnnouncementComplete);

      logger.logStoryEvent('backend_announcement_setup_completed', {
        story_id: story.id,
        story_title: story.title,
        vad_disabled_on_server: true,
        awaiting_response_completion: true
      }, userAge, conversationId);

    } catch (error) {
      logger.logErrorEvent('backend_announcement_flow_failed', error as Error, {
        story_id: story.id,
        story_title: story.title,
        conversation_id: conversationId
      }, userAge, conversationId);
      throw error;
    }
  }, [voiceLiveClient]);

  // Handle story selection
  const handleStorySelect = useCallback(async (story: Story | null) => {
    setCurrentStory(story);
    
    // Reset conversation state to idle when selecting a new story
    // This ensures mic button starts in ready state (not listening)
    setConversationState('idle');
    setIsRecording(false);
    setIsContinuousListening(false);
    
    if (story && userAge && conversationId) {
      logger.logUIEvent('story_selected', {
        story_id: story.id,
        story_title: story.title,
        reading_level: story.reading_level
      }, userAge, conversationId);
      
      // Clear any previous errors
      setCurrentError(null);
      
      // ========================================================================
      // CLEAR PREVIOUS STORY STATE ON BACKEND
      // ========================================================================
      // Clear reading progress tracker on backend for clean slate
      try {
        const clearResponse = await fetch(getApiUrl(`/voicelive/reading/session/${conversationId}`), {
          method: 'DELETE'
        });
        
        if (clearResponse.ok) {
          const clearData = await clearResponse.json();
          logger.logUIEvent('backend_reading_session_cleared', {
            conversation_id: conversationId,
            new_story_id: story.id,
            success: clearData.success
          }, userAge, conversationId);
          console.log('✅ Backend reading session cleared for new story');
        }
      } catch (clearError) {
        logger.logErrorEvent('failed_to_clear_backend_reading_session', clearError as Error, {
          conversation_id: conversationId,
          story_id: story.id
        }, userAge, conversationId);
        // Don't block story selection if clear fails
        console.warn('Failed to clear backend reading session, continuing anyway:', clearError);
      }
      // ========================================================================
      
      // CREATE STORY SESSION for independent tracking
      // This creates a new session record for this story while keeping the same conversationId
      try {
        const storySession = await backendVoiceLiveService.createStorySession({
          conversation_id: conversationId,
          story_id: story.id.toString(),
          story_title: story.title,
          age: userAge
        });
        
        logger.logVoiceLiveEvent('story_session_created', {
          session_id: storySession.session_id,
          conversation_id: conversationId,
          story_id: story.id,
          story_title: story.title
        }, userAge, conversationId);
        
        console.log(`📝 Story session created: ${storySession.session_id} for story "${story.title}"`);
        
      } catch (sessionError) {
        logger.logErrorEvent('failed_to_create_story_session', sessionError as Error, {
          conversation_id: conversationId,
          story_id: story.id
        }, userAge, conversationId);
        // Don't block story selection if session creation fails
        console.warn('Failed to create story session, continuing anyway:', sessionError);
      }
      
      // Update story context with VoiceLive and trigger announcement
      try {
        // Set VoiceLive client reference in story context service
        storyContextService.setVoiceLiveClient(voiceLiveClient);
        
        // Set user context in VoiceLive client for topic monitoring and pronunciation assessment
        voiceLiveClient.setUserContext(userAge, story.id, story.content);
        
        // Handle complete story selection flow: context update + announcement
        const success = await storyContextService.handleStorySelection(story, userAge, conversationId);
        
        if (success) {
          logger.logStoryEvent('story_context_integration_success', {
            story_id: story.id,
            story_title: story.title,
            context_updated: true,
            announcement_triggered: true
          }, userAge, conversationId);
          
          // Auto-trigger VoiceLive conversation with backend-controlled announcement
          if (voiceLiveClient.isConnected()) {
            try {
              // Check if browser requires explicit user gesture for microphone access
              const needsUserGesture = requiresUserGestureForMicrophone();
              const browserName = getBrowserName();
              
              logger.logAudioEvent('browser_detected_for_mic_access', {
                browser: browserName,
                requires_user_gesture: needsUserGesture,
                story_id: story.id
              }, userAge, conversationId);
              
              if (needsUserGesture) {
                // Safari/iOS: Show "Start Reading" button for user to tap
                console.log(`🍎 ${browserName} detected - requiring user gesture for microphone access`);
                setPendingStoryForMicAccess(story);
                setShowStartReadingButton(true);
                
                logger.logAudioEvent('safari_user_gesture_required', {
                  story_id: story.id,
                  browser: browserName,
                  message: 'Showing Start Reading button for user tap'
                }, userAge, conversationId);
                
                return; // Exit early - user must tap button to proceed
              }
              
              // Chrome/Firefox: Auto-start microphone (original behavior)
              const setupAudioAndTriggerWithBackend = async () => {
                try {
                  // Request microphone permission if not already granted
                  const stream = await navigator.mediaDevices.getUserMedia({ 
                    audio: {
                      echoCancellation: true,  // ✅ Browser-level AEC
                      noiseSuppression: true,  // ✅ Reduce background noise
                      autoGainControl: true    // ✅ Consistent volume
                    }
                  });
                  // Stop the stream immediately after permission check
                  stream.getTracks().forEach(track => track.stop());
                  
                  logger.logAudioEvent('microphone_permission_verified_for_backend_story', {
                    story_id: story.id,
                    backend_controlled: true,
                    echo_cancellation: true,
                    noise_suppression: true,
                    auto_gain_control: true
                  }, userAge, conversationId);
                  
                  // Enable child-friendly mode for more responsive interaction
                  voiceLiveClient.enableChildFriendlyMode();
                  
                  // Keep in push-to-talk mode for story reading with pronunciation assessment
                  // User must click mic button to start reading
                  setIsContinuousMode(false);
                  
                  // Use backend-controlled announcement to prevent interruptions
                  await handleStoryAnnouncementWithBackend(story, userAge, conversationId);
                  
                  console.log(`📢 Backend-controlled story announcement started for "${story.title}"`);
                  
                  // Show ready notification after a brief delay
                  // setTimeout(() => {
                  //   setShowReadyNotification(true);
                  //   // Auto-hide the notification after 5 seconds
                  //   setTimeout(() => setShowReadyNotification(false), 3000);
                  // }, 2000); // Show notification 2 seconds after introduction trigger
                  
                } catch (audioError) {
                  logger.logErrorEvent('microphone_setup_failed_for_backend_story', audioError as Error, {
                    story_id: story.id,
                    backend_controlled: true
                  }, userAge, conversationId);
                  
                  console.warn('Microphone setup failed, but triggering backend announcement anyway:', audioError);
                  
                  // Still trigger backend-controlled announcement even if mic setup fails
                  try {
                    await handleStoryAnnouncementWithBackend(story, userAge, conversationId);
                  } catch (backendError) {
                    console.error('Backend announcement also failed, falling back to direct trigger:', backendError);
                    voiceLiveClient.triggerStoryIntroduction();
                  }
                }
              };
              
              // Add a short delay to allow context updates to apply, then setup audio and trigger
              setTimeout(setupAudioAndTriggerWithBackend, 1000); // 1 second delay to ensure context is properly applied
              
            } catch (error) {
              logger.logErrorEvent('backend_story_introduction_failed', error as Error, {
                story_id: story.id,
                backend_controlled: true
              }, userAge, conversationId);
              
              console.warn('Failed to trigger backend-controlled story introduction, falling back to direct:', error);
              // Fallback to direct trigger if backend fails
              voiceLiveClient.triggerStoryIntroduction();
            }
          } else {
            console.log('VoiceLive not connected, skipping auto-introduction');
          }
          
          console.log(`Story "${story.title}" selected successfully with context integration`);
        } else {
          logger.logErrorEvent('story_context_integration_failed', new Error('Story context service returned false'), {
            story_id: story.id
          }, userAge, conversationId);
          
          // Don't show error to user if offline fallback worked
          console.warn('Story context integration failed, but story selection should still work');
        }
      } catch (error) {
        logger.logErrorEvent('story_context_integration_error', error as Error, {
          story_id: story.id,
          story_title: story.title
        }, userAge, conversationId);
        
        // Only show error if it's a critical failure
        const errorMessage = (error as Error).message;
        if (!errorMessage.includes('backend unavailable')) {
          setCurrentError(`Story selection failed: ${errorMessage}`);
        }
        console.error('Failed to update story context:', error);
      }
    } else if (!story && userAge && conversationId) {
      // Clear story context when deselecting story
      try {
        // Stop continuous listening and switch back to push-to-talk mode
        voiceLiveClient.stopContinuousListening();
        setIsContinuousMode(false);
        setIsContinuousListening(false);
        
        await storyContextService.clearStoryContext(userAge, conversationId);
        logger.logUIEvent('story_deselected', {
          previous_story: currentStory?.id || 'none'
        }, userAge, conversationId);
      } catch (error) {
        logger.logErrorEvent('story_deselection_error', error as Error, {
          previous_story: currentStory?.id || 'none'
        }, userAge, conversationId);
        
        // Don't bother user with deselection errors unless critical
        console.error('Failed to clear story context:', error);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAge, conversationId, voiceLiveClient, currentStory, setCurrentError]);
  
  // Handle reading mode changes
  const handleReadingModeChange = useCallback(async (newReadingState: StoryReadingState) => {
    setReadingState(newReadingState);
    
    // Update VoiceLive configuration if connected
    if (voiceLiveClient.isConnected() && userAge) {
      try {
        if (newReadingState.isReadingMode && newReadingState.currentStory) {
          // Enable reading mode - extra patient VAD settings for natural reading pauses
          voiceLiveClient.enableReadingMode();
          
          // Try to get reading-specific instructions from backend (optional)
          try {
            const readingConfigResponse = await fetch(getApiUrl(`/stories/age/${userAge}/reading-config`));
            if (readingConfigResponse.ok) {
              const readingConfig = await readingConfigResponse.json();
              
              if (readingConfig.reading_mode_instructions) {
                // Append reading instructions to existing instructions
                const currentInstructions = voiceLiveClient.getSessionConfig()?.instructions || '';
                voiceLiveClient.updateSessionConfig({
                  instructions: `${currentInstructions}\n\n${readingConfig.reading_mode_instructions}`
                });
                console.log('Provided Assistant instructions - '+currentInstructions);
              }
            }
          } catch (configError) {
            // Ignore if reading config endpoint doesn't exist - reading mode still enabled
            console.log('Reading config not available from backend, using default reading mode');
          }
          
          logger.logUIEvent('reading_mode_enabled', {
              story_id: newReadingState.currentStory.id,
              reading_mode_activated: true
            }, userAge || undefined, conversationId);
        } else {
          // Disable reading mode and return to conversation mode
          voiceLiveClient.disableReadingMode();
          
          logger.logUIEvent('reading_mode_disabled', {}, userAge || undefined, conversationId);
        }
      } catch (error) {
        console.error('Error updating reading mode:', error);
        logger.logErrorEvent('reading_mode_update_failed', error as Error, {
          reading_mode: newReadingState.isReadingMode
        }, userAge || undefined, conversationId);
      }
    }
  }, [voiceLiveClient, userAge, conversationId]);
  
  // Start new conversation
  const handleNewConversation = () => {
    setMessages([]);
    logger.logUIEvent('new_conversation_started', {}, userAge || undefined, conversationId);
  };
  
  // Clear error
  const clearError = () => {
    setCurrentError(null);
  };

  // Handle "Start Reading" button click (Safari/iOS user gesture requirement)
  const handleStartReading = useCallback(async () => {
    if (!pendingStoryForMicAccess || !userAge) return;
    
    const story = pendingStoryForMicAccess;
    
    try {
      console.log('🎤 User tapped Start Reading - requesting microphone access with user gesture');
      
      // Now we have user gesture - request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,  // ✅ Browser-level AEC (critical for Safari/iOS)
          noiseSuppression: true,  // ✅ Reduce background noise
          autoGainControl: true    // ✅ Consistent volume
        }
      });
      stream.getTracks().forEach(track => track.stop());
      
      logger.logAudioEvent('safari_user_gesture_microphone_granted', {
        story_id: story.id,
        browser: getBrowserName(),
        echo_cancellation: true,
        noise_suppression: true,
        auto_gain_control: true
      }, userAge, conversationId);
      
      // Hide the button
      setShowStartReadingButton(false);
      setPendingStoryForMicAccess(null);
      
      // Enable child-friendly mode for more responsive interaction
      voiceLiveClient.enableChildFriendlyMode();
      
      // Keep in push-to-talk mode for story reading with pronunciation assessment
      // User must click mic button to start reading
      setIsContinuousMode(false);
      
      // Use backend-controlled announcement
      await handleStoryAnnouncementWithBackend(story, userAge, conversationId);
      
      console.log(`📢 Backend-controlled story announcement started for "${story.title}"`);
      
      // Show ready notification
      setTimeout(() => {
        // setShowReadyNotification(true);
        // setTimeout(() => setShowReadyNotification(false), 3000);
      }, 2000);
      
    } catch (error) {
      logger.logErrorEvent('safari_microphone_access_denied', error as Error, {
        story_id: story.id,
        browser: getBrowserName()
      }, userAge, conversationId);
      
      setCurrentError('Microphone access is required for voice reading. Please allow microphone access and try again.');
      setShowStartReadingButton(false);
      setPendingStoryForMicAccess(null);
    }
  }, [pendingStoryForMicAccess, userAge, conversationId, voiceLiveClient, handleStoryAnnouncementWithBackend]);

  // Handle quiz answer submission
  const handleQuizAnswer = async (answer: string, isCorrect: boolean) => {
    if (!quizQuestion) return;
    
    const timeTaken = quizAnswerStartTime 
      ? (Date.now() - quizAnswerStartTime) / 1000 
      : undefined;
    
    // ✅ NEW: Show validating state
    setIsValidatingQuiz(true);
    setQuizValidationResult(null);
    
    try {
      // Submit to backend and wait for response
      const response = await backendVoiceLiveService.submitQuizAnswer({
        conversation_id: conversationId,
        question_id: quizQuestion.question_id,
        selected_answer: answer,
        time_taken_seconds: timeTaken
      });
      
      console.log('✅ Quiz answer validation response:', response);
      
      // ✅ NEW: Update validation result based on backend response
      setIsValidatingQuiz(false);
      setQuizValidationResult(response.correct ? 'correct' : 'incorrect');
      
      // Log interaction
      logger.logVoiceLiveEvent('quiz_answer_user_interaction', {
        question_id: quizQuestion.question_id,
        correct: response.correct,
        time_taken: timeTaken,
        milestone: quizQuestion.milestone
      }, userAge || undefined, conversationId);
      
      if (response.correct) {
        // ✅ NEW: Show next question from queue after correct answer
        setTimeout(() => {
          const nextIndex = currentQuestionIndex + 1;
          if (nextIndex < quizQuestionQueue.length) {
            // More questions in queue - show next one
            const nextQuestion = quizQuestionQueue[nextIndex];
            setCurrentQuestionIndex(nextIndex);
            setQuizQuestion(nextQuestion);
            setQuizAnswerStartTime(Date.now());
            setQuizValidationResult(null);
            console.log(`✅ Moving to question ${nextIndex + 1}/${quizQuestionQueue.length}:`, nextQuestion.question_id);
          } else {
            // No more questions - wait for completion phrase or timeout
            // QuizCritter will say "Happy Reading!" and trigger persona switch
            console.log('✅ All questions answered - waiting for completion phrase');
            setQuizValidationResult(null);
            // Don't dismiss yet - wait for "Happy Reading" detection
          }
        }, 2000); // Show feedback for 2 seconds before next question
      } else {
        // Incorrect answer - reset validation state after feedback animation
        setTimeout(() => {
          setQuizValidationResult(null);
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to submit quiz answer:', error);
      setIsValidatingQuiz(false);
      setQuizValidationResult('incorrect'); // Treat error as incorrect
      
      logger.logErrorEvent('quiz_answer_submission_failed', error as Error, {
        question_id: quizQuestion?.question_id
      }, userAge || undefined, conversationId);
    }
  };

  // Handle quiz skip
  const handleQuizSkip = () => {
    setQuizVisible(false);
    setQuizQuestion(null);
    setQuizQuestionQueue([]);
    setCurrentQuestionIndex(0);
    
    logger.logVoiceLiveEvent('quiz_question_skipped', {
      question_id: quizQuestion?.question_id,
      milestone: quizQuestion?.milestone
    }, userAge || undefined, conversationId);
  };

  // Manual reconnection
  const handleManualReconnect = async () => {
    if (userAge !== null) {
      try {
        await handleAgeSelected(userAge);
      } catch (error) {
        console.error('Manual reconnection failed:', error);
        setCurrentError('Manual reconnection failed. Please try again.');
      }
    }
  };

  // Toggle continuous listening
  const handleToggleListening = async () => {
    try {
      await voiceLiveClient.toggleContinuousListening();
      logger.logAudioEvent('continuous_listening_toggled', {
        was_listening: isContinuousListening,
        now_listening: !isContinuousListening,
        story_id: currentStory?.id
      }, userAge || undefined, conversationId);
    } catch (error) {
      logger.logErrorEvent('continuous_listening_toggle_failed', error as Error, {
        story_id: currentStory?.id
      }, userAge || undefined, conversationId);
    }
  };
  
  // Determine which screen to show
  if (userRole === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        {/* Header with Version Display */}
        <div className="flex justify-end p-4">
          <VersionDisplay variant="badge" className="bg-white/80 backdrop-blur-sm shadow-sm" />
        </div>
        
        {/* Role Selector */}
        <div className="flex-1 flex items-center justify-center p-4">
          <RoleSelector onRoleSelected={handleRoleSelected} />
        </div>
      </div>
    );
  }
  
  if (userRole === 'teacher') {
    return <EnhancedTeacherDashboard onReturnToHome={handleReturnToHome} />;
  }
  
  if (userAge === null || !ageProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        {/* Header with Version Display */}
        <div className="flex justify-end p-4">
          <VersionDisplay variant="badge" className="bg-white/80 backdrop-blur-sm shadow-sm" />
        </div>
        
        {/* Age Selector */}
        <div className="flex-1 flex items-center justify-center p-4">
          <AgeSelector onAgeSelected={handleAgeSelected} onReturnToHome={handleReturnToHome} />
        </div>
        
        {currentError && (
          <div className="fixed bottom-4 right-4 bg-red-500 text-white p-4 rounded-lg shadow-lg">
            <p>{currentError}</p>
            <button onClick={clearError} className="mt-2 text-sm underline">
              Dismiss
            </button>
          </div>
        )}
      </div>
    );
  }
  
  // Show WelcomeScreen only for initial connection, not for reconnections
  // Include 'error' state to show error messages before first successful connection
  if ((connectionState === 'disconnected' || connectionState === 'connecting' || connectionState === 'error') && !hasEverConnected) {
    return (
      <div className={`min-h-screen ${ageProfile.theme.background} flex items-center justify-center p-4`}>
        <WelcomeScreen 
          ageProfile={ageProfile} 
          connectionState={connectionState}
          onRetry={() => handleAgeSelected(userAge)}
          errorMessage={currentError || undefined}
        />
      </div>
    );
  }
  
  // Main conversation interface
  return (
    <div className={`min-h-screen ${ageProfile.theme.background} flex flex-col relative`}>
      {/* Enhanced Topic Drift Indicator */}
      <TopicDriftIndicator 
        isActive={isTopicDrifting}
        ageProfile={ageProfile}
        contentSimilarity={topicDriftData.contentSimilarity}
        storyRelevance={topicDriftData.storyRelevance}
        driftScore={topicDriftData.driftScore}
        onDismiss={() => setIsTopicDrifting(false)}
      />
      
      {/* Connection Status Indicator - shows during reconnections */}
      <ConnectionStatusIndicator 
        connectionState={connectionState} 
        onReconnect={handleManualReconnect}
      />
      
      {/* Header */}
      <header className={`${ageProfile.theme.primary} text-white p-4 shadow-lg`}>
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <h1 className={`${ageProfile.theme.text} font-bold`}>
              {ageProfile.ui.appName}
            </h1>
            {/* Story Time Section in Header */}
            <div className="flex items-center gap-3 bg-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
              <span className="text-sm font-medium">
                📚 Story Time
              </span>
              {currentStory && (
                <span className="text-xs bg-white/20 rounded-full px-2 py-1">
                  {currentStory.title}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Version Display */}
            <VersionDisplay variant="badge" className="bg-white/10 backdrop-blur-sm" />
            
            {/* Home Button */}
            <button
              onClick={handleReturnToHome}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors border border-white/20"
              title="Return to Role Selection"
            >
              <HomeIcon className="w-4 h-4 mr-2" />
              Home
            </button>
            <StatusIndicator 
              connectionState={connectionState}
              conversationState={conversationState}
              ageProfile={ageProfile}
            />
            {/* Conversation Panel Toggle with Tooltip */}
            <div className="relative">
              <button
                onClick={() => {
                  const uiStateService = uiStateManager.getInstance(conversationId);
                  uiStateService.controlFloatingPanel('toggle', 'user_action');
                  
                  // Local fallback - toggle panel immediately
                  setShowConversationPanel(!showConversationPanel);
                  setShowChatTooltip(false); // Hide tooltip when clicked
                }}
                className={`p-2 rounded-full transition-colors ${
                  showConversationPanel 
                    ? 'bg-white/20 hover:bg-white/30' 
                    : 'bg-white/10 hover:bg-white/20'
                }`}
                aria-label="Toggle conversation panel"
              >
                <ChatIcon className="w-6 h-6" />
                
                {/* Notification badge */}
                {isTopicDrifting && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                )}
                
                {/* Message count badge */}
                {messages.length > 0 && (
                  <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {messages.length > 9 ? '9+' : messages.length}
                  </span>
                )}
              </button>

              {/* Chat Button Tooltip */}
              {ageProfile && (
                <ChatButtonTooltip
                  isVisible={showChatTooltip}
                  ageProfile={ageProfile}
                  position="left"
                  onComplete={() => setShowChatTooltip(false)}
                />
              )}
            </div>
            <button
              onClick={() => setShowApiTest(!showApiTest)}
              className={`p-2 rounded-full transition-colors ${
                showApiTest 
                  ? 'bg-white/20 hover:bg-white/30' 
                  : 'bg-white/10 hover:bg-white/20'
              }`}
              aria-label="Toggle API Test"
              title="Toggle API Connectivity Test"
            >
              <MonitorIcon className="w-6 h-6" />
              {/* API Test indicator dot */}
              {showApiTest && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
              )}
            </button>

          </div>
        </div>
      </header>
      
      {/* Main Content - Story Pane with proper spacing for floating button */}
      <main className="flex-1 flex min-h-0">
        {/* Story Pane - Adjusted width to account for floating microphone */}
        <div className={`flex-1 flex flex-col min-h-0 p-6 ${
          microphoneFloating && microphonePosition === 'middle-right' 
            ? 'pr-24' // Increased padding-right to prevent overlap with floating mic button
            : ''
        }`}>
          <StoryPane
            ageProfile={ageProfile}
            userAge={userAge}
            conversationId={conversationId}
            isRecording={conversationState === 'listening' || isContinuousListening}
            onStorySelect={handleStorySelect}
            onReadingModeChange={handleReadingModeChange}
            voiceLiveClient={voiceLiveClient}
            quizDialogEnabled={quizDialogEnabled}
            onQuizDialogEnabledChange={setQuizDialogEnabled}
            roleRangerEnabled={roleRangerEnabled}
            onRoleRangerEnabledChange={setRoleRangerEnabled}
            pronunciationWsClientRef={pronunciationWsClientRef}
          />
        </div>
      </main>

      {/* Floating Conversation Panel - Bottom Right */}
      {showConversationPanel && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 pointer-events-none">
          <div 
            className="fixed bottom-4 right-4 w-96 max-h-[70vh] bg-white/95 backdrop-blur-md shadow-2xl rounded-2xl border border-purple-200 pointer-events-auto transform transition-all duration-500 ease-out animate-slide-up"
            style={{
              animation: 'slideUpFromBottom 0.5s ease-out',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(139, 92, 246, 0.3)'
            }}
          >
            <ConversationPane 
              messages={messages}
              ageProfile={ageProfile}
              conversationState={conversationState}
              onNewConversation={handleNewConversation}
              conversationId={conversationId}
              isFloating={true}
              isVisible={showConversationPanel}
              hideEmptyState={readingState.isReadingMode} 
              onToggleVisibility={() => {
                const uiStateService = uiStateManager.getInstance(conversationId);
                uiStateService.controlFloatingPanel('hide', 'user_action');
                
                // Local fallback - hide panel immediately
                setShowConversationPanel(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Safari/iOS: Start Reading Button (User Gesture Required) */}
      {showStartReadingButton && pendingStoryForMicAccess && ageProfile && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`${ageProfile.theme.background} rounded-2xl shadow-2xl max-w-md w-full p-8 text-center`}>
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <PowerIcon className="w-10 h-10 text-white" />
              </div>
              <h2 className={`${ageProfile.theme.typography.headingSize} font-bold text-gray-800 mb-2`}>
                Ready to Start Reading?
              </h2>
              <p className={`${ageProfile.theme.typography.bodySize} text-gray-600 mb-1`}>
                <strong>{pendingStoryForMicAccess.title}</strong>
              </p>
              <p className={`${ageProfile.theme.typography.bodySize} text-gray-500`}>
                Tap the button below to allow microphone access and start your story adventure!
              </p>
            </div>
            
            <button
              onClick={handleStartReading}
              className="w-full py-4 px-6 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              style={{
                WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
                touchAction: 'manipulation'
              }}
            >
              🎤 Start Reading
            </button>
            
            <button
              onClick={() => {
                setShowStartReadingButton(false);
                setPendingStoryForMicAccess(null);
              }}
              className="mt-3 text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Floating Microphone Button */}
      {microphoneFloating && (
        <>          
          <div 
            className={`fixed z-50 ${
              microphonePosition === 'middle-right' ? 'top-1/2 right-4 -translate-y-1/2' :
              microphonePosition === 'bottom-right' ? 'bottom-8 right-4' :
              'bottom-8 left-1/2 -translate-x-1/2'
            }`}
            style={{
              // Ensure microphone doesn't overlap with conversation panel or story content
              ...(microphonePosition === 'middle-right' && {
                right: showConversationPanel ? '420px' : '16px' // Account for conversation panel width
              })
            }}
          >
            <div className="transform hover:scale-105 transition-all duration-300 drop-shadow-2xl">
              <MicButton
                isRecording={isRecording}
                conversationState={conversationState}
                ageProfile={ageProfile}
                voiceLiveClient={voiceLiveClient}
                isListening={isContinuousListening}
                onToggleListening={handleToggleListening}
                isContinuousMode={isContinuousMode}
                onPronunciationFrame={handlePronunciationFrame}
              />
            </div>
          </div>
        </>
      )}
      

      {/* Error Toast */}
      {currentError && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white p-4 rounded-lg shadow-lg max-w-md z-50">
          <p className="text-sm">{currentError}</p>
          <button 
            onClick={clearError} 
            className="mt-2 text-xs underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Quiz Dialog - QuizCritter Comprehension Questions */}
      {/* Only show if quizDialogEnabled preference is true AND quiz is triggered */}
      {quizQuestion && quizDialogEnabled && (
        <QuizDialog
          question={quizQuestion.question_text}
          options={quizQuestion.options}
          questionId={quizQuestion.question_id}
          milestone={quizQuestion.milestone}
          correctAnswer={quizQuestion.correct_answer}
          onAnswer={handleQuizAnswer}
          onSkip={handleQuizSkip}
          visible={quizVisible}
          isValidating={isValidatingQuiz}
          validationResult={quizValidationResult}
          isCompleting={quizCompleting}
        />
      )}

      {/* API Test Component - Conditional rendering based on toggle */}
      {showApiTest && <ApiTestComponent onClose={() => setShowApiTest(false)} />}
      
      {/* Custom Animations */}
      <style>{`
        @keyframes slideUpFromBottom {
          0% {
            transform: translateY(100%) scale(0.95);
            opacity: 0;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
        
        @keyframes slideDownToBottom {
          0% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
          100% {
            transform: translateY(100%) scale(0.95);
            opacity: 0;
          }
        }
        
        .animate-slide-up {
          animation: slideUpFromBottom 0.5s ease-out;
        }
        
        .animate-slide-down {
          animation: slideDownToBottom 0.3s ease-in;
        }
        
        /* Floating microphone button hover effects */
        .floating-mic-button {
          filter: drop-shadow(0 10px 30px rgba(139, 92, 246, 0.3));
        }
        
        .floating-mic-button:hover {
          filter: drop-shadow(0 15px 40px rgba(139, 92, 246, 0.4));
        }
      `}</style>
    </div>
  );
}